// src/views/PublicTopBar.jsx
import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { publicSiteWebService } from "../services/publicSiteWebService";
import defaultLogo from "../assets/images/logo.png";

export default function PublicTopBar() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    publicSiteWebService.getPublicData()
      .then(data => {
        if (data.parametres?.logo) setLogoUrl(data.parametres.logo);
      })
      .catch(() => {
        // Échec silencieux — on garde le logo statique par défaut
      });
  }, []);

  // Si l'utilisateur est connecté, on cache la barre de navigation publique
  if (user) return null;

  return (
    <nav className={`ptb ${scrolled ? 'ptb-scrolled' : ''}`} role="navigation" aria-label="Navigation publique">
      <div className="ptb-row">
        <NavLink to="/" className="ptb-brand">
          <img src={logoUrl || defaultLogo} alt="Enumera" className="ptb-logo" />
        </NavLink>

        <button
          className="ptb-burger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={menuOpen}
        >
          <i className={`ti ${menuOpen ? "ti-x" : "ti-menu-2"}`} aria-hidden="true" />
        </button>

        <div className="ptb-links">
          <NavLink to="/" className="ptb-link" end>
            <i className="ti ti-home" aria-hidden="true" /> Accueil
          </NavLink>
          <NavLink to="/features" className="ptb-link">
            <i className="ti ti-layout-grid" aria-hidden="true" /> Fonctionnalités
          </NavLink>
          <NavLink to="/contact" className="ptb-link">
            <i className="ti ti-mail" aria-hidden="true" /> Contact
          </NavLink>
          <div className="ptb-divider" />
          <NavLink to="/login" className="ptb-link ptb-link-secondary">
            <i className="ti ti-login" aria-hidden="true" /> Connexion
          </NavLink>
          <NavLink to="/register" className="ptb-link ptb-link-primary">
            <i className="ti ti-user-plus" aria-hidden="true" /> S'inscrire
          </NavLink>
        </div>
      </div>

      {menuOpen && (
        <div className="ptb-mobile-menu">
          <NavLink to="/" className="ptb-mobile-link" onClick={() => setMenuOpen(false)}>
            <i className="ti ti-home" /> Accueil
          </NavLink>
          <NavLink to="/features" className="ptb-mobile-link" onClick={() => setMenuOpen(false)}>
            <i className="ti ti-layout-grid" /> Fonctionnalités
          </NavLink>
          <NavLink to="/contact" className="ptb-mobile-link" onClick={() => setMenuOpen(false)}>
            <i className="ti ti-mail" /> Contact
          </NavLink>
          <div className="ptb-mobile-divider" />
          <NavLink to="/login" className="ptb-mobile-link ptb-mobile-link-secondary" onClick={() => setMenuOpen(false)}>
            <i className="ti ti-login" /> Connexion
          </NavLink>
          <NavLink to="/register" className="ptb-mobile-link ptb-mobile-link-primary" onClick={() => setMenuOpen(false)}>
            <i className="ti ti-user-plus" /> S'inscrire
          </NavLink>
        </div>
      )}

      <style>{`
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

        /* Palette Kamalpha (dupliquée ici : AdminLayout n'est pas monté hors connexion) */
        .ptb {
          --pr:    #1B4F80;
          --pr-lt: #2F6DA8;
          --pr-dk: #0E2F52;
          --pr-bg: #E6EEF7;
          --or:    #C08A2E;
          --or-lt: #E3B75A;
          --or-dk: #8A5A14;

          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: linear-gradient(90deg, var(--pr-dk) 0%, var(--pr) 100%);
          padding: 14px 40px;
          transition: all 0.3s ease;
        }

        /* Filet dégradé bleu → or : la signature du logo */
        .ptb::after {
          content: "";
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--pr) 0%, var(--pr-lt) 55%, var(--or) 100%);
        }

        .ptb-scrolled {
          padding: 10px 40px;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.18);
        }

        .ptb-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1280px;
          margin: 0 auto;
        }

        .ptb-brand {
          display: flex;
          align-items: center;
          text-decoration: none;
        }

        /* Même traitement que dans la sidebar admin : logo sur fond blanc, liseré or */
        .ptb-logo {
          height: 40px;
          width: auto;
          padding: 4px 8px;
          background: #ffffff;
          border: 1px solid rgba(192, 138, 46, 0.5);
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .ptb-scrolled .ptb-logo {
          height: 36px;
        }

        .ptb-burger {
          display: none;
          border: none;
          background: rgba(255, 255, 255, 0.12);
          color: #ffffff;
          font-size: 24px;
          padding: 8px 10px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .ptb-burger:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .ptb-links {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .ptb-divider {
          width: 1px;
          height: 30px;
          background: rgba(227, 183, 90, 0.35);
          margin: 0 8px;
        }

        .ptb-link {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 500;
          font-size: 14px;
          text-decoration: none;
          color: rgba(255, 255, 255, 0.85);
          transition: all 0.2s ease;
        }

        .ptb-link i {
          font-size: 16px;
        }

        .ptb-link:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.12);
        }

        .ptb-link.active {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.14);
          font-weight: 600;
          box-shadow: inset 0 -3px 0 var(--or);
        }

        .ptb-link.active i {
          color: var(--or-lt);
        }

        /* Bouton primaire : or clair, texte bleu foncé (bon contraste) */
        .ptb-link-primary {
          background: var(--or-lt);
          color: var(--pr-dk) !important;
          font-weight: 600;
          padding: 8px 20px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
        }

        .ptb-link-primary:hover {
          background: #ecc677;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.22);
          transform: translateY(-2px);
          color: var(--pr-dk) !important;
        }

        /* Bouton secondaire */
        .ptb-link-secondary {
          background: rgba(255, 255, 255, 0.08);
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .ptb-link-secondary:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #ffffff !important;
          border-color: var(--or-lt);
        }

        /* Menu mobile */
        .ptb-mobile-menu {
          display: none;
          margin-top: 12px;
          background: #ffffff;
          border-radius: 16px;
          padding: 12px;
          border: 1px solid rgba(192, 138, 46, 0.35);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .ptb-mobile-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 10px;
          color: #0f1b2d;
          text-decoration: none;
          transition: all 0.2s ease;
          font-weight: 500;
        }

        .ptb-mobile-link:hover {
          background: var(--pr-bg);
          color: var(--pr);
        }

        .ptb-mobile-link.active {
          background: var(--pr-bg);
          color: var(--pr);
          font-weight: 600;
          box-shadow: inset 3px 0 0 var(--or);
        }

        .ptb-mobile-link i {
          font-size: 18px;
          width: 24px;
          text-align: center;
          color: var(--pr);
        }

        .ptb-mobile-divider {
          height: 1px;
          background: rgba(27, 79, 128, 0.12);
          margin: 4px 0;
        }

        .ptb-mobile-link-primary {
          background: var(--pr);
          color: #ffffff !important;
          margin-top: 4px;
          box-shadow: 0 2px 10px rgba(27, 79, 128, 0.25);
        }

        .ptb-mobile-link-primary i {
          color: var(--or-lt);
        }

        .ptb-mobile-link-primary:hover {
          background: var(--pr-dk) !important;
          color: #ffffff !important;
          box-shadow: 0 4px 20px rgba(27, 79, 128, 0.35);
          transform: translateY(-1px);
        }

        .ptb-mobile-link-secondary {
          background: var(--pr-bg);
          color: var(--pr) !important;
          border: 1px solid rgba(27, 79, 128, 0.15);
        }

        .ptb-mobile-link-secondary:hover {
          background: rgba(27, 79, 128, 0.14) !important;
          color: var(--pr) !important;
        }

        /* === RESPONSIVE === */
        @media (max-width: 768px) {
          .ptb { padding: 10px 20px; }
          .ptb-scrolled { padding: 8px 20px; }
          .ptb-logo { height: 32px; }
          .ptb-scrolled .ptb-logo { height: 30px; }
          .ptb-burger { display: block; }
          .ptb-links { display: none; }
          .ptb-mobile-menu { display: flex; flex-direction: column; }
          .ptb-divider { display: none; }
        }

        @media (max-width: 480px) {
          .ptb { padding: 8px 16px; }
          .ptb-scrolled { padding: 6px 16px; }
          .ptb-logo { height: 28px; }
          .ptb-scrolled .ptb-logo { height: 26px; }
          .ptb-mobile-link { font-size: 14px; padding: 10px 14px; }
          .ptb-mobile-link i { font-size: 16px; width: 20px; }
        }
      `}</style>
    </nav>
  );
}